import {
  Component,
  ChangeDetectionStrategy,
  input,
  ChangeDetectorRef,
  inject,
  effect,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TimelineModule } from 'primeng/timeline';
import { Store } from '@ngrx/store';
import { COMPANY_INFO, SIGNUPSTEP_INFO } from '../../state/app.selector';
import { LANGUAGE, PayLinkStepOption } from 'models';
import { map, Observable } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { PayLinkSteps } from 'enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SELECT_LANGUAGE } from '../../state/actions/company.action';
import { LangdropdownPaylinkComponent } from '../components/langdropdown-paylink/langdropdown-paylink.component';
import { MultilingualNamePipe } from '../pipes/multilingual-name.pipe';

@Component({
  standalone: true,
  selector: 'ff-paylink-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    CommonModule,
    TimelineModule,
    LangdropdownPaylinkComponent,
    TranslateModule,
    MultilingualNamePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  public store = inject(Store);
  public translate = inject(TranslateService);
  private cd = inject(ChangeDetectorRef);

  showTimeline = input<boolean>(true);
  currentStep = input<PayLinkSteps>(PayLinkSteps.Details);

  // Convert store select to signal
  private companyInfoSignal = toSignal(this.store.select(COMPANY_INFO), {
    initialValue: null,
  });
  companyDetails = signal<any>(null);

  constructor() {
    // Effect to handle company info changes and language setup
    effect(() => {
      const responseData = this.companyInfoSignal();
      if (responseData) {
        this.companyDetails.set(responseData.compDetails);
        if (responseData?.selectedLang && responseData.selectedLang.LANG_CODE) {
          this.translate.setDefaultLang(responseData.selectedLang.LANG_CODE);
          this.translate.use(responseData.selectedLang.LANG_CODE);
        } else {
          // Fallback to English if no language is selected
          this.translate.setDefaultLang('en');
          this.translate.use('en');
        }
      } else {
        // Reset company details when company info is null
        this.companyDetails.set(null);
      }
    });
  }

  // Keep Observable for template compatibility if needed
  get companyDetails$(): Observable<any> {
    return this.store.select(COMPANY_INFO);
  }

  get paylinkSignUpSteps$(): Observable<any[]> {
    return this.store.select(SIGNUPSTEP_INFO).pipe(
      map((o1) => {
        // eslint-disable-next-line prefer-const
        let steps: PayLinkStepOption[] = o1.steps;
        return steps.map((o: any, index: number) => ({
          ...this.getTitleNode(o),
          stepIndex: index + 1,
        }));
      }),
    );
  }

  /** Aligns marker visuals with signup-timeline (current / done / pending). */
  timelineMarkerKind(event: {
    isCurrentPage?: boolean;
    status?: boolean;
  }): 'current' | 'done' | 'pending' {
    if (!event) return 'pending';
    if (event.isCurrentPage) return 'current';
    if (event.status) return 'done';
    return 'pending';
  }

  getTitleNode(x: PayLinkStepOption): any {
    const currentPayLinkSteps = this.currentStep();
    if (
      x.name == PayLinkSteps.Details ||
      x.name.toString() == PayLinkSteps[PayLinkSteps.Details]
    ) {
      const isCurrentPage: boolean =
        currentPayLinkSteps == PayLinkSteps.Details ||
        currentPayLinkSteps.toString() == PayLinkSteps[PayLinkSteps.Details];
      return {
        title: PayLinkSteps[PayLinkSteps.Details],
        status: x.isProcessed || isCurrentPage,
        isCurrentPage: isCurrentPage,
      };
    } else if (
      x.name == PayLinkSteps.Waiver ||
      x.name.toString() == PayLinkSteps[PayLinkSteps.Waiver]
    ) {
      const isCurrentPage: boolean =
        currentPayLinkSteps == PayLinkSteps.Waiver ||
        currentPayLinkSteps.toString() == PayLinkSteps[PayLinkSteps.Waiver];
      return {
        title: PayLinkSteps[PayLinkSteps.Waiver],
        status: x.isProcessed || isCurrentPage,
        isCurrentPage: isCurrentPage,
      };
    } else if (
      x.name == PayLinkSteps.Information ||
      x.name.toString() == PayLinkSteps[PayLinkSteps.Information]
    ) {
      const isCurrentPage: boolean =
        currentPayLinkSteps == PayLinkSteps.Information ||
        currentPayLinkSteps.toString() ==
          PayLinkSteps[PayLinkSteps.Information];
      return {
        title: PayLinkSteps[PayLinkSteps.Information],
        status: x.isProcessed || isCurrentPage,
        isCurrentPage: isCurrentPage,
      };
    } else if (
      x.name == PayLinkSteps.Payment ||
      (x.name as PayLinkSteps).toString() == PayLinkSteps[PayLinkSteps.Payment]
    ) {
      const isCurrentPage: boolean =
        currentPayLinkSteps == PayLinkSteps.Payment ||
        currentPayLinkSteps.toString() == PayLinkSteps[PayLinkSteps.Payment];
      return {
        title: PayLinkSteps[PayLinkSteps.Payment],
        status: x.isProcessed || isCurrentPage,
        isCurrentPage: isCurrentPage,
      };
    } else {
      return {};
    }
  }

  selectedLang(e: LANGUAGE) {
    localStorage.setItem('DEFAULT_LANGUAGE', JSON.stringify(e));
    localStorage.setItem('selectedLanguage', e.LANG_CODE);
    this.store.dispatch(SELECT_LANGUAGE({ lang: e }));
    this.translate.use(e.LANG_CODE);
    this.cd.markForCheck();
  }

  get isCompanyStateFound() {
    const details = this.companyDetails();
    return details !== undefined && details !== null && details.uuid !== '';
  }
}
